import { useMutation, useQueryClient } from '@tanstack/react-query';
import { callStartNegotiatorReport } from './callStartNegotiatorReport';
import { callGetJobStatus } from '../core';

interface GenerateNegotiatorReportVariables {
  companyId?: string;
  startDate: string;
  endDate: string;
  password?: string;
}

interface GenerateNegotiatorReportResponse {
  shareUrl?: string;
  jobId?: string;
  jobIds?: string[];
  totalCompanies?: number;
}

const pollJobStatus = (jobId: string): Promise<Record<string, unknown>> => {
  return new Promise((resolve, reject) => {
    const poll = async () => {
      const res = await callGetJobStatus(jobId);
      if (res.code !== 200 || !res.data) {
        setTimeout(poll, 2500);
        return;
      }
      const status = res.data.status;
      if (status === 'completed') {
        resolve((res.data.result as Record<string, unknown>) || {});
        return;
      }
      if (status === 'failed') {
        reject(new Error(res.data.errorMessage || 'Negotiator report generation failed'));
        return;
      }
      setTimeout(poll, 2500);
    };
    setTimeout(poll, 2500);
  });
};

export const useGenerateNegotiatorReport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: GenerateNegotiatorReportVariables) => {
      const response = await callStartNegotiatorReport(data);
      if (response.code !== 202) {
        throw new Error(
          (response.data as { message?: string } | undefined)?.message ||
            'Failed to start negotiator report generation'
        );
      }

      const resData = response.data as any;

      // Multiple companies — jobs are queued, no need to poll
      if (resData.jobIds) {
        return {
          data: {
            jobIds: resData.jobIds,
            totalCompanies: resData.totalCompanies
          }
        } as { data: GenerateNegotiatorReportResponse };
      }

      // Single company — poll until done
      if (!resData.jobId) {
        throw new Error('Failed to start negotiator report generation');
      }
      const result = await pollJobStatus(resData.jobId);
      return { data: result } as { data: GenerateNegotiatorReportResponse };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['artifacts'] });
    }
  });
};

export default useGenerateNegotiatorReport;
